import { useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from '@tanstack/react-router';
import { centavos, formatMoney, newId } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { emitir, emitirYVer } from '../lib/emitir.js';
import { clienteLlamado } from '../lib/pedidos.js';
import { usePuede } from '../lib/sesion.js';
import { coincideTelefono } from '../lib/telefono.js';
import { coincide } from '../lib/texto.js';
import { ordenarListas } from '../lib/catalogo.js';
import { Campo } from '../ui/Campo.js';
import { CLIENTE_VACIO, DIAS, FormularioCliente, camposDe, saldoAnteriorDe } from '../ui/FormularioCliente.js';
import { Modal } from '../ui/Modal.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { useT } from '../i18n/index.js';

/**
 * Los clientes.
 *
 * A row opens the client's own page (`ClienteDetalle`): what they owe, the fiado and the abonos
 * that got it there, and their details to edit. The list itself is for finding somebody, and
 * for the one question the notebook behind the counter always answered at a glance — who owes.
 */
export function Clientes() {
  const t = useT();
  const clientes = useLiveQuery(() => db.clientes.toArray(), [], []);
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const pedidos = useLiveQuery(() => db.pedidos.toArray(), [], []);
  const puede = usePuede('gestionar_clientes');
  const puedeDarCredito = usePuede('dar_credito');
  const puedeElegirLista = usePuede('elegir_lista');

  const [consulta, setConsulta] = useState('');
  /** «Nuevo»'s sheet, over the list: forms in a sheet, not above the rest (the owner, 2026-10-06). */
  const [nuevo, setNuevo] = useState(false);
  const [b, setB] = useState(CLIENTE_VACIO);
  const [soloDeben, setSoloDeben] = useState(false);
  /**
   * What went wrong saving, said in the sheet; and «Guardar» pressed once, since a second press
   * while the first was saved made the same client twice (2026-10-13). Nor is one already called
   * that saved again, which splits their fiado in two (`clienteLlamado`).
   */
  const [error, setError] = useState<string | null>(null);
  const enviado = useRef(false);
  const yaEsta = nuevo ? clienteLlamado(clientes, b.nombre) : undefined;

  const activas = ordenarListas(listas);

  async function guardar() {
    if (!b.nombre.trim() || yaEsta || enviado.current) return;
    enviado.current = true;
    setError(null);
    const clienteId = newId();
    try {
      const { rechazo } = await emitirYVer('cliente.creado', {
        clienteId,
        ...camposDe(b, { limite: puedeDarCredito, lista: puedeElegirLista, cedula: true }),
      });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      // What the notebook says they already owed, when they are new to the app but not to the shop.
      const anterior = saldoAnteriorDe(b);
      if (puedeDarCredito && anterior) await emitir('cliente.saldo_inicial', { clienteId, monto: anterior });
      setNuevo(false);
      setB(CLIENTE_VACIO);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      enviado.current = false;
    }
  }

  const activos = clientes.filter((c) => c.activo);
  /**
   * Everybody who owes, a client taken off the list too: what they owe is still owed. Counted only
   * among the active, the total was short of what «Fiado» says (2026-10-13), and who owed the rest
   * was in neither list.
   */
  const deben = clientes.filter((c) => (c.saldo ?? 0) > 0);
  const totalFiado = centavos(deben.reduce((a, c) => a + (c.saldo ?? 0), 0));

  const visibles = (soloDeben ? deben : activos)
    .filter((c) => !consulta.trim() || coincide(c.nombre, consulta) || coincideTelefono(c.telefono, consulta))
    // Owing, the most first; then everybody else by name.
    .sort((a, x) =>
      soloDeben
        ? (x.saldo ?? 0) - (a.saldo ?? 0)
        : a.nombre.localeCompare(x.nombre, 'es'),
    );

  return (
    <Pagina
      titulo={t('clientes.titulo')}
      subtitulo={
        totalFiado > 0
          ? t('clientes.activosYFiado', { n: activos.length, monto: formatMoney(totalFiado) })
          : t('clientes.activos', { n: activos.length })
      }
      acciones={
        puede && (
          <button
            type="button"
            className="btn-primario gap-1.5 px-4 text-sm"
            data-guia="clientes-nuevo"
            onClick={() => {
              setB(CLIENTE_VACIO);
              setError(null);
              setNuevo(true);
            }}
          >
            <Ico icono="nuevo" className="size-4" />
            {t('comun.nuevo')}
          </button>
        )
      }
    >
      {nuevo && (
        <Modal titulo={t('pos.clienteNuevo')} onCerrar={() => setNuevo(false)}>
          <FormularioCliente
            b={b}
            setB={setB}
            listas={activas}
            puedeDarCredito={puedeDarCredito}
            puedeElegirLista={puedeElegirLista}
            conCedula
            enHoja
            yaEsta={yaEsta}
            onGuardar={() => void guardar()}
          />
          {error && (
            <p role="alert" className="mt-3 text-sm text-rose-400">
              {error}
            </p>
          )}
        </Modal>
      )}

      <div className="mb-4 flex gap-2">
        <Campo
          etiqueta={t('comun.buscar')}
          value={consulta}
          onChange={(e) => setConsulta(e.target.value)}
          placeholder={t('pos.nombreOTelefono')}
          autoComplete="off"
          className="min-w-0"
        />
      </div>

      {deben.length > 0 && (
        <div className="mb-4 flex gap-1 rounded-xl bg-slate-800 p-1">
          {([false, true] as const).map((v) => (
            <button
              key={String(v)}
              type="button"
              onClick={() => setSoloDeben(v)}
              data-sin-icono="pestana"
              className={`flex-1 rounded-lg px-3 py-2 text-sm transition ${
                soloDeben === v ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`}
            >
              {v ? t('clientes.deben', { n: deben.length }) : t('clientes.todos')}
            </button>
          ))}
        </div>
      )}

      {visibles.length === 0 ? (
        <Vacio mensaje={t('clientes.ninguno')} icono="clientes" />
      ) : (
        <ul className="escalonar flex flex-col gap-2">
          {visibles.map((c) => {
            const saldo = c.saldo ?? 0;
            const pendientes = pedidos.filter((p) => p.clienteId === c.id && p.estado === 'pendiente').length;
            return (
              <li key={c.id} className="tarjeta flex items-center justify-between gap-3">
                <Link
                  to="/clientes/$clienteId"
                  params={{ clienteId: c.id }}
                  className="tarjeta-abrir min-w-0 flex-1"
                  data-sin-icono="fila"
                >
                  <p className="truncate font-medium">{c.nombre}</p>
                  <p className="mt-0.5 truncate text-xs text-slate-500">
                    {[
                      c.activo ? null : t('clientes.desactivado'),
                      t(
                        c.tipo === 'mayorista'
                          ? 'clientes.etiquetaMayorista'
                          : 'clientes.etiquetaMostrador',
                      ),
                      activas.find((l) => l.id === c.listaId)?.nombre,
                      c.diaVisita !== null ? t(DIAS[c.diaVisita]!) : null,
                      c.telefono,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {saldo !== 0 && (
                    <p className={`tabular mt-1 text-xs font-medium ${saldo > 0 ? 'text-amber-300' : 'text-emerald-300'}`}>
                      {saldo > 0
                        ? t('fiado.debe', { monto: formatMoney(c.saldo!) })
                        : t('fiado.aFavor', { monto: formatMoney(centavos(-saldo)) })}
                    </p>
                  )}
                </Link>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-xs text-slate-500">{pendientes || ''}</span>
                  <Link
                    to="/clientes/pedidos/nuevo"
                    search={{ cliente: c.id }}
                    className="btn-secundario gap-1 px-3 text-xs"
                  >
                    <Ico icono="pedidos" className="size-3.5" />
                    {t('clientes.pedido')}
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Pagina>
  );
}
